import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { MouseEvent, useState } from 'react';
import AddressEditDialog from './address-edit-dialog';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SharedProfileInformationAccordion from '../../../../shared/shared-profile-information-accordion';
import SharedProfileInformationItemActions from '../../../../shared/shared-profile-information-item-actions';

interface AddressAccordionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  setValue: UseFormSetValue<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  getValues: UseFormGetValues<UserFormData>;
  disabled: boolean;
}

export default function AddressAccordion({
  control,
  errors,
  disabled,
  watch,
  setValue,
  getValues,
}: AddressAccordionProps) {
  const [openAddressDialog, setOpenAddressDialog] = useState(false);
  const [clickedIndex, setClickedIndex] = useState(0);

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'addresses',
  });

  const menuItems = [
    { value: 'custom', label: 'Custom' },
    { value: 'home', label: 'Home' },
    { value: 'other', label: 'Other' },
    { value: 'work', label: 'Work' },
  ];

  const addressErrors = errors['addresses'];
  const addressCount = fields.length;

  const handleOnAdd = (
    event: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation();
    append({
      type: '',
      customType: '',
      sourceIsStructured: false,
      formatted: '',
      poBox: '',
      extendedAddress: '',
      streetAddress: '',
      locality: '',
      region: '',
      postalCode: '',
      country: '',
      primary: false,
    });
  };

  return (
    <>
      <SharedProfileInformationAccordion
        title="Addresses"
        count={addressCount}
        hasError={!!addressErrors}
        onAdd={handleOnAdd}
        disabled={disabled}
      >
        {fields.map((item, index) => {
          const formattedAddress = watch(`addresses.${index}.formatted`);
          const typeField = watch(`addresses.${index}.type`);

          return (
            <Box key={item.id}>
              <Stack
                gap={1}
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ my: 1.5 }}
              >
                <Stack flexDirection="row" alignItems="center" gap={2}>
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`addresses.${index}.type`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `addresses-type-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `addresses-type-${index}`,
                        }}
                        name="addresses"
                        select
                        disabled={disabled}
                        label={'Type'}
                        error={!!addressErrors?.[index]?.type}
                        helperText={
                          addressErrors?.[index]?.type
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 150,
                        }}
                      >
                        {menuItems.map((item) => (
                          <MenuItem value={item.value} key={item.label}>
                            {item.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                  {typeField === 'custom' && (
                    <Controller
                      rules={{ required: 'This field is required.' }}
                      name={`addresses.${index}.customType`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          required
                          inputRef={field.ref}
                          inputProps={{
                            id: `addresses-customType-${index}`,
                          }}
                          InputLabelProps={{
                            htmlFor: `addresses-customType-${index}`,
                          }}
                          disabled={disabled}
                          name="addresses"
                          label={`Custom Type`}
                          error={!!addressErrors?.[index]?.customType}
                          helperText={
                            addressErrors?.[index]?.customType
                              ? 'This field is required.'
                              : ''
                          }
                          sx={{
                            minWidth: 160,
                          }}
                        />
                      )}
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      whiteSpace: 'pre-line',
                    }}
                  >
                    {`${formattedAddress}`}
                  </Typography>
                </Stack>

                <SharedProfileInformationItemActions
                  index={index}
                  totalCount={addressCount}
                  disabled={disabled}
                  onMoveDown={() => move(index, index + 1)}
                  onMoveUp={() => move(index, index - 1)}
                  onEdit={() => {
                    setClickedIndex(index);
                    setOpenAddressDialog(true);
                  }}
                  editTooltipLabel="Edit Address"
                  onDelete={() => remove(index)}
                />
              </Stack>
              {index !== fields.length - 1 && <Divider />}
            </Box>
          );
        })}
      </SharedProfileInformationAccordion>
      {openAddressDialog ? (
        <AddressEditDialog
          openAddressDialog={openAddressDialog}
          setOpenAddressDialog={setOpenAddressDialog}
          clickedIndex={clickedIndex}
          setValue={setValue}
          disabled={!!disabled}
          getValues={getValues}
        />
      ) : null}
    </>
  );
}
